import config from '@/config';
import { defineStore } from 'pinia';
import platformClient, { type Models } from 'purecloud-platform-client-v2';
import { USER_DISPLAY_NAMES } from '@/constants';

const client = platformClient.ApiClient.instance;
client.setEnvironment(config.genesysCloud.region);
client.setPersistSettings(true, 'genesys-cuca-ai-app');
const usersApi = new platformClient.UsersApi();

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isLoggedIn: false,
    agent: null as Models.UserMe | null,
    userId: null as string | null,
    isInitializing: false,
    error: null as string | null,
    conversationId: null as string | null,
    language: null as string | null,
  }),

  getters: {
    isAuthenticated: (state) => state.isLoggedIn && state.agent !== null,
    agentName: (state) => state.agent?.name || USER_DISPLAY_NAMES.AGENT,
  },

  actions: {
    /**
     * Initialize app and handle authentication via SDK.
     */
    async initialize() {
      this.isInitializing = true;
      this.error = null;

      try {
        const clientId = config.genesysCloud.clientId;
        if (!clientId) throw new Error('Client ID not found in configuration.');

        const cleanRedirectUri = window.location.origin;
        const urlParams = new URLSearchParams(window.location.search);
        const initialCId = urlParams.get('cId');
        const initialLang = urlParams.get('lang');

        // SDK handles token persistence and redirects automatically.
        // Pass initial params as 'state' to survive redirect.
        const encodedState =
          initialCId && initialLang
            ? btoa(JSON.stringify({ cId: initialCId, lang: initialLang }))
            : undefined;

        const authData = await client.loginImplicitGrant(
          clientId,
          cleanRedirectUri,
          { state: encodedState }
        );

        // Determine conversation parameters source.
        // Priority 1: OAuth redirect state
        if (authData?.state) {
          this._processOAuthState(authData.state);
          if (this.error) {
            throw new Error(this.error);
          }
        }
        // Priority 2: Initial URL parameters
        else if (initialCId && initialLang) {
          this.conversationId = initialCId;
          this.language = initialLang;
        }

        // Validate required parameters after processing
        if (!this.conversationId || !this.language) {
          throw new Error(
            'Missing required parameters: cId and lang are required after authentication.'
          );
        }

        // Authentication successful
        this.isLoggedIn = true;
        this.agent = await usersApi.getUsersMe();
        this.userId = this.agent.id || null;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : 'Authentication failed';
        console.error('Authentication failed:', err);
      } finally {
        this.isInitializing = false;
      }
    },

    /**
     * Process OAuth redirect state parameter.
     */
    _processOAuthState(returnedState: string) {
      let decoded: string;
      try {
        decoded = atob(returnedState);
      } catch (e) {
        console.error('Failed to decode base64 state from SDK response:', e);
        this.error = 'Failed to decode authentication state (invalid base64).';
        return;
      }

      let decodedState: { cId?: string; lang?: string };
      try {
        decodedState = JSON.parse(decoded);
      } catch (e) {
        console.error('Failed to parse JSON from decoded state:', e);
        this.error = 'Failed to parse authentication state (invalid JSON).';
        return;
      }

      this.conversationId = decodedState.cId || null;
      this.language = decodedState.lang || null;

      // Clean URL by removing auth fragments
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  },
});
